/** @format */

import React, {createContext, useContext, useEffect, useState} from 'react'

import LoaderButton from '@src/components/tailwind/LoaderButton'

import ajax from '../ajax'
import CancelModal from '../components/tailwind/CancelModal'
import {AccountContext} from '../contexts/AccountContext'
import * as toast from '../toast'
import AccountModal from './AccountModal'
import {BillingModal} from './BillingModal'
import {STEPS} from './constants'
import NeedMoreModal from './NeedMoreModal'
import {PurchaseModal} from './PurchaseModal'
import {billingPeriodForPlan} from './utils'

const amplitude = window.amplitude

export const RESET_CHECKOUT = {
  currentModal: null,
  currentStep: STEPS.NOTHING,
  productId: null, // this is the product id, ie. "gold" or "platinum"
  checkoutData: {
    email: '',
    password: '',
    passwordcheck: '',
    firstName: '',
    lastName: '',
    name: '',
    address: '',
    city: '',
    state: '',
    country: 'US',
    zip: '',
    period: 'yearly',
    stripePlan: null, // this is the plan in Stripe, ie "v1_gold_yearly"
    why: '',
  },
}

export const CheckoutContext = createContext({})

export function CheckoutContextProvider(props) {
  const {account, sub, card} = useContext(AccountContext)
  const [state, setState] = useState(RESET_CHECKOUT)

  const openModalFn =
    (prod, prefix = '') =>
    e => {
      e.preventDefault()

      amplitude.logEvent('Clicked Select Plan', {plan: prod.id})

      if (!account) {
        window.location.href = '/register'
        return
      }

      // Paid users clicking "Free Forever" go to billing page for proper cancellation flow
      if (prod.id === 'free' && account.product !== 'free') {
        window.location.href = '/account/billing'
        return
      }

      let firstStep = STEPS.REGISTER

      if (prod.id === 'free') {
        firstStep = STEPS.CANCEL
      } else if (prod.id === 'custom') {
        firstStep = STEPS.SALES
      } else if (account && sub && card) {
        firstStep = STEPS.PAYMENT
      } else if (account) {
        firstStep = STEPS.PERSONAL
      }

      setState(s => {
        let result = {
          ...RESET_CHECKOUT,
          currentModal: prefix + prod.id,
          currentStep: firstStep,
          productId: prod.id,
        }
        result.checkoutData.period = s.checkoutData.period
        result.checkoutData.stripePlan = prod[s.checkoutData.period].stripe_plan
        return result
      })
    }

  const closeModal = e => {
    e && e.preventDefault()
    setState(s => ({
      ...s,
      currentModal: null,
      currentStep: STEPS.NOTHING,
    }))
  }

  const nextStep = async e => {
    e.preventDefault()

    var valid = true

    function check(fieldName) {
      if (!state.checkoutData[fieldName]) {
        toast.warning(`Missing field "${fieldName}".`)
        valid = false
      }
    }

    switch (state.currentStep) {
      case STEPS.REGISTER:
        ;['email', 'password'].forEach(check)

        if (state.checkoutData.password !== state.checkoutData.passwordcheck) {
          toast.warning(`Passwords don't match.`)
          valid = false
        }

        if (valid) {
          amplitude.logEvent('Clicked Checkout Next', {step: 'register'})
          setState(s => ({...s, currentStep: s.currentStep + 1}))
        }
        break
      case STEPS.PERSONAL:
        ;['name', 'address', 'country'].forEach(check)

        if (valid) {
          amplitude.logEvent('Clicked Checkout Next', {step: 'personal'})
          setState(s => ({...s, currentStep: s.currentStep + 1}))
        }
        break
    }
  }

  const prevStep = e => {
    e.preventDefault()
    setState(s => ({...s, currentStep: s.currentStep - 1}))
  }

  const updateCheckoutData = (keyOrObj, value) => {
    // either immediately sets a value, or returns an event handler function
    if (value !== undefined) {
      setState(s => ({
        ...s,
        checkoutData: {...s.checkoutData, [keyOrObj]: value},
      }))
    } else if (typeof keyOrObj === 'string') {
      // just a key is passed, so return a function that can accept a value
      return e => {
        e.persist()
        setState(s => ({
          ...s,
          checkoutData: {...s.checkoutData, [keyOrObj]: e.target.value},
        }))
      }
    } else {
      // must be an object with key/value pairs
      setState(s => ({
        ...s,
        checkoutData: {...s.checkoutData, ...keyOrObj},
      }))
    }
  }

  // Open the toggle on the period the account is billed on: CheckoutButton's
  // `current` check compares account.plan against the plan id of whichever
  // period is showing, so on the other one a customer's own card reads
  // "Select".
  //
  // The plan comes from the document — serve_plans injects account_plan for a
  // logged-in visitor — rather than from /api-int/account, which answers around
  // half a second after the page is revealed. Once, on mount: the period is
  // the visitor's to change from there.
  //
  // From an effect rather than useState's initializer because /plans is
  // prerendered logged out, so the first render has to match markup built with
  // no account. The state this queues commits before markReactReady's frame
  // callback reveals the page, which is what keeps the switch off screen —
  // same arrangement as PlansPage's `?recommended=` read.
  useEffect(() => {
    const period = billingPeriodForPlan(
      window.formspree.account_plan,
      window.formspree.products,
    )
    if (period) {
      updateCheckoutData('period', period)
    }
  }, [])

  return (
    <CheckoutContext.Provider
      value={{
        currentModal: state.currentModal,
        openModalFn,
        closeModal,
        currentStep: state.currentStep,
        nextStep,
        prevStep,
        checkoutData: state.checkoutData,
        updateCheckoutData,
      }}
    >
      {props.children}
    </CheckoutContext.Provider>
  )
}

export function CheckoutButton(props) {
  const {
    prod,
    prefix = '',
    noMobileScroll = false,
    fullWidth = true,
    narrow = false,
  } = props
  const {account, projects, sub, reloadAccount} = useContext(AccountContext)
  const {currentModal, openModalFn, closeModal, currentStep, checkoutData} =
    useContext(CheckoutContext)

  const resubscribe = async () => {
    await ajax({
      endpoint: '/api-int/resubscribe',
      onSuccess: async () => {
        closeModal()
        await reloadAccount()
      },
      errorMsg: 'Failed to resubscribe',
      successMsg: r => {
        return (
          'Glad to have you back! ' +
          `Your subscription will now automatically renew on ${r.at}`
        )
      },
    })
  }

  const renderCancelModal = (productId, prefix, noMobileScroll) => {
    return (
      <CancelModal
        sub={sub}
        isOpen={
          currentModal === prefix + prod.id && currentStep === STEPS.CANCEL
        }
        onSuccess={closeModal}
        onClose={closeModal}
        noMobileScroll={noMobileScroll}
      />
    )
  }

  let current = account && account.plan === prod[checkoutData.period]?.stripe_plan
  let canceling = sub && sub.cancel_at_period_end
  let unavailable =
    account &&
    (account.form_count > prod.monthly.limits.forms ||
      projects.length > prod.monthly.limits.projects ||
      account.member_count > prod.monthly.limits.team_members)
  let buttonId = prod.id + '-plan-button'
  let buttonClass =
    'mt-8 block buttonPrimary w-full' +
    (fullWidth ? '' : ' lg:w-1/4') +
    (narrow ? ' text-sm' : '')
  let checkoutEnabled = prod.id !== 'free' && prod.id !== 'custom'

  if (unavailable) {
    return (
      <button
        className={buttonClass + ' tooltip hint--top'}
        data-hint="Current account exceeds this plan's limits"
        onClick={() => {}}
        disabled={true}
      >
        Over Limit
      </button>
    )
  } else if (current && canceling) {
    return (
      <LoaderButton className={buttonClass} onClick={resubscribe}>
        Resume
      </LoaderButton>
    )
  } else {
    return (
      <>
        <div className="left">
          {checkoutEnabled && (
            <AccountModal
              productId={prod.id}
              prefix={prefix}
              noMobileScroll={noMobileScroll}
              key={prefix + prod.id + 'account-modal-top'}
            />
          )}
          {checkoutEnabled && (
            <BillingModal
              productId={prod.id}
              prefix={prefix}
              noMobileScroll={noMobileScroll}
            />
          )}
          {checkoutEnabled && (
            <PurchaseModal
              product={prod}
              prefix={prefix}
              noMobileScroll={noMobileScroll}
            />
          )}
          {account &&
            prod.id === 'free' &&
            renderCancelModal(prod.id, prefix, noMobileScroll)}
          {prod.id === 'custom' && (
            <NeedMoreModal
              productId={prod.id}
              prefix={prefix}
              noMobileScroll={noMobileScroll}
            />
          )}
        </div>
        <button
          id={buttonId}
          className={buttonClass}
          onClick={openModalFn(prod, prefix)}
          disabled={
            prod.id === 'free' ? current || canceling : current && !canceling
          }
        >
          {current && !canceling
            ? 'Current'
            : canceling && prod.id === 'free'
              ? 'Canceling'
              : prod.id === 'free' && !account
                ? 'Get Started'
                : prod.id === 'custom'
                  ? 'Contact Sales'
                  : 'Select'}
        </button>
      </>
    )
  }
}
